import React, { useState, useEffect } from 'react';
import { Package, AlertTriangle, Calendar, Edit2, Plus, Minus, Check, X, Loader2, History, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import Pagination from './Pagination';
import { updateProductStock, fetchStockMovements } from '../api';

/* ── Edit Stock Modal ── */
const EditStockModal = ({ product, onClose, onSave }) => {
  const [mode, setMode] = useState('add'); // 'set' | 'add' | 'reduce'
  const [value, setValue] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const currentStock = product?.stock ?? 0;

  const preview = () => {
    const n = parseInt(value, 10);
    if (isNaN(n) || n < 0) return null;
    if (mode === 'set') return n;
    if (mode === 'add') return currentStock + n;
    if (mode === 'reduce') return Math.max(0, currentStock - n);
    return null;
  };

  const handleSave = async () => {
    const n = parseInt(value, 10);
    if (isNaN(n) || n < 0) { setError('Enter a valid positive number'); return; }
    setError('');
    setSaving(true);
    try {
      let quantity, movementType;
      if (mode === 'set') {
        const diff = n - currentStock;
        quantity = diff;
        movementType = diff >= 0 ? 'adjustment' : 'adjustment';
      } else if (mode === 'add') {
        quantity = n;
        movementType = 'purchase';
      } else {
        if (n > currentStock) { setError(`Cannot reduce by more than current stock (${currentStock})`); setSaving(false); return; }
        quantity = -n;
        movementType = 'adjustment';
      }
      await updateProductStock(product.id, { quantity, movementType, notes: notes || `Manual stock ${mode}` });
      onSave(product.id, preview());
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to update stock');
    } finally {
      setSaving(false);
    }
  };

  if (!product) return null;

  const tabs = [
    { k: 'set',    label: 'Set Stock',    icon: Edit2,  color: '#2563eb', bg: '#eff6ff', border: 'border-blue-200/40' },
    { k: 'add',    label: 'Add Stock',    icon: Plus,   color: '#10b981', bg: '#ecfdf5', border: 'border-emerald-200/40' },
    { k: 'reduce', label: 'Reduce Stock', icon: Minus,  color: '#f43f5e', bg: '#fff1f2', border: 'border-rose-200/40' },
  ];

  const activeTab = tabs.find(t => t.k === mode);
  const newStock = preview();

  return (
    <div className="ui-modal-overlay">
      <div className="bg-white rounded-[28px] shadow-2xl w-full max-w-md border border-slate-100/80 animate-fade-in">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-slate-100">
          <div>
            <h3 className="text-base font-bold text-slate-800 tracking-tight">Edit Stock Level</h3>
            <p className="text-xs text-slate-400 font-semibold mt-1">{product.name}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer">
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Current Stock */}
          <div className="flex items-center justify-between bg-slate-50 border border-slate-200/40 rounded-2xl px-4 py-3">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Current Stock</span>
            <span className="text-lg font-extrabold text-slate-800">{currentStock} {product.stockUnit}</span>
          </div>

          {/* Mode Tabs */}
          <div className="flex gap-2">
            {tabs.map(tab => {
              const Icon = tab.icon;
              const active = mode === tab.k;
              return (
                <button key={tab.k} onClick={() => { setMode(tab.k); setValue(''); setError(''); }}
                  className="flex-1 flex flex-col items-center gap-1.5 py-3 rounded-2xl border-2 transition-all text-xs font-bold uppercase tracking-wider cursor-pointer"
                  style={{
                    borderColor: active ? tab.color : '#f1f5f9',
                    background: active ? tab.bg : '#fff',
                    color: active ? tab.color : '#94a3b8',
                  }}>
                  <Icon size={14} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Value Input */}
          <div>
            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
              {mode === 'set' ? 'New Stock Quantity' : mode === 'add' ? 'Quantity to Add' : 'Quantity to Reduce'}
            </label>
            <input
              type="number"
              min="0"
              value={value}
              onChange={e => { setValue(e.target.value); setError(''); }}
              placeholder="0"
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 font-bold text-base bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-colors"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Notes / Remarks</label>
            <input
              type="text"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="e.g., Damaged items, new supplier batch..."
              className="ui-input w-full"
            />
          </div>

          {/* Preview */}
          {newStock !== null && (
             <div className="flex items-center justify-between rounded-xl px-4 py-3 border"
              style={{ borderColor: activeTab.color + '40', background: activeTab.bg }}>
              <span className="text-xs font-bold" style={{ color: activeTab.color }}>Predicted Stock After Update</span>
              <span className="text-lg font-extrabold" style={{ color: activeTab.color }}>
                {newStock}
              </span>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-rose-50 border border-rose-100 rounded-xl px-4 py-3 text-xs text-rose-700 font-bold leading-relaxed">{error}</div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-bold text-xs uppercase tracking-wider hover:bg-slate-200 transition-colors cursor-pointer">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving || value === ''}
              className="flex-1 py-2.5 rounded-xl text-white font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
              style={{ background: saving ? '#94a3b8' : `linear-gradient(135deg, ${activeTab?.color}, ${activeTab?.color}d0)` }}>
              {saving ? <Loader2 size={14} className="animate-spin text-white" /> : <Check size={14} />}
              <span>{saving ? 'Saving...' : 'Update'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── Stock History Modal ── */
const StockHistoryModal = ({ product, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!product) return;
    const loadHistory = async () => {
      try {
        setLoading(true);
        const data = await fetchStockMovements(product.id);
        setHistory(data);
        setError('');
      } catch (err) {
        setError('Failed to load stock history');
      } finally {
        setLoading(false);
      }
    };
    loadHistory();
  }, [product]);

  if (!product) return null;

  return (
    <div className="ui-modal-overlay">
      <div className="bg-white rounded-[28px] shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col border border-slate-100/80 animate-fade-in">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-purple-50 text-purple-600 rounded-xl shadow-sm border border-purple-150">
              <History size={18} />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-800 tracking-tight">Stock History Log</h3>
              <p className="text-xs text-slate-400 font-semibold mt-1">{product.name} (Current: {product.stock})</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1 ui-scrollbar">
          {loading ? (
             <div className="flex flex-col items-center justify-center py-12 space-y-3">
               <Loader2 size={32} className="text-purple-500 animate-spin" />
               <p className="text-xs text-slate-400 font-bold uppercase tracking-wider">Loading stock movements...</p>
             </div>
          ) : error ? (
             <div className="bg-rose-50 text-rose-700 p-4 rounded-2xl text-xs font-semibold border border-rose-100/40 text-center">
               {error}
             </div>
          ) : history.length === 0 ? (
             <div className="text-center py-12 border border-dashed border-slate-200 rounded-3xl bg-slate-50">
               <p className="text-slate-400 font-semibold text-sm">No stock movements recorded yet.</p>
             </div>
          ) : (
            <div className="space-y-4">
              {history.map((record, i) => {
                const date = new Date(record.created_at);
                const isAddition = record.quantity_change > 0;
                return (
                  <div key={record.id || i} className="flex gap-4 items-start p-4 hover:bg-slate-50/50 rounded-2xl border border-slate-100 transition-all duration-150">
                    <div className={`p-2.5 rounded-full shrink-0 shadow-sm border ${isAddition ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-rose-50 text-rose-600 border-rose-100'}`}>
                      {isAddition ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1.5 gap-2">
                        <span className="font-extrabold text-slate-800 text-sm flex items-center gap-2">
                          {isAddition ? '+' : ''}{record.quantity_change}
                          <span className="text-[9px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200/40 text-slate-500 capitalize">
                            {record.movement_type?.replace(/_/g, ' ')}
                          </span>
                        </span>
                        <span className="text-[10px] font-bold text-slate-400 tabular-nums">
                          {date.toLocaleString()}
                        </span>
                      </div>
                      <div className="text-xs font-semibold text-slate-500">
                        Stock changed from <strong className="text-slate-700 font-extrabold">{record.previous_quantity}</strong> to <strong className="text-slate-700 font-extrabold">{record.new_quantity}</strong>
                      </div>
                      {record.notes && (
                        <div className="mt-2.5 text-xs text-slate-600 bg-slate-50 border border-slate-200/50 rounded-xl p-3 italic">
                          " {record.notes} "
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Main Inventory Component ── */
const Inventory = ({ products: initialProducts, categories: categoriesProp = [], onProductStockUpdate }) => {
  const [products, setProducts] = useState(initialProducts || []);
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [editingProduct, setEditingProduct] = useState(null);
  const [historyProduct, setHistoryProduct] = useState(null);
  const itemsPerPage = 10;

  // Keep in sync with parent prop changes
  React.useEffect(() => { setProducts(initialProducts || []); }, [initialProducts]);

  const categoryNamesFromApi = categoriesProp.map((c) => c.name || c.category_name || c.title).filter(Boolean);
  const categoryNamesFromProducts = [...new Set(products.map((p) => p.category).filter(Boolean))];
  const allCategoryNames = categoryNamesFromApi.length > 0
    ? [...new Set([...categoryNamesFromApi, ...categoryNamesFromProducts])]
    : categoryNamesFromProducts;
  const categories = ['all', ...allCategoryNames.sort((a, b) => a.localeCompare(b))];

  const getDaysUntilExpiry = (expiryDate) =>
    Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));

  const getFilteredProducts = () => {
    let filtered = products;
    if (categoryFilter !== 'all') filtered = filtered.filter(p => p.category === categoryFilter);
    switch (filter) {
      case 'lowStock':   return filtered.filter(p => p.stock < 40 && p.stock > 0);
      case 'outOfStock': return filtered.filter(p => p.stock === 0);
      case 'expiring':   return filtered.filter(p => new Date(p.expiryDate) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000));
      default:           return filtered;
    }
  };

  const filteredProducts = getFilteredProducts();
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + itemsPerPage);

  const handleStockSave = (productId, newStock) => {
    setProducts(prev => prev.map(p => p.id === productId ? { ...p, stock: newStock } : p));
    if (onProductStockUpdate) onProductStockUpdate(productId, newStock);
  };

  // Safe color maps for dynamic classes to avoid Tailwind bundle exclusion issues
  const colorMap = {
    blue: {
      bg: 'bg-gradient-to-br from-white to-blue-50/20 border-blue-100/30',
      iconBg: 'bg-gradient-to-br from-blue-500 to-blue-600 text-white',
      badge: 'bg-blue-100/60 text-blue-700 border-blue-200/40',
      text: 'text-slate-800'
    },
    green: {
      bg: 'bg-gradient-to-br from-white to-emerald-50/20 border-emerald-100/30',
      iconBg: 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white',
      badge: 'bg-emerald-100/60 text-emerald-700 border-emerald-200/40',
      text: 'text-slate-800'
    },
    orange: {
      bg: 'bg-gradient-to-br from-white to-amber-50/20 border-amber-100/30',
      iconBg: 'bg-gradient-to-br from-amber-500 to-amber-600 text-white',
      badge: 'bg-amber-100/60 text-amber-700 border-amber-200/40',
      text: 'text-amber-750'
    },
    red: {
      bg: 'bg-gradient-to-br from-white to-rose-50/20 border-rose-100/30',
      iconBg: 'bg-gradient-to-br from-rose-500 to-rose-600 text-white',
      badge: 'bg-rose-100/60 text-rose-700 border-rose-200/40',
      text: 'text-rose-600'
    },
    yellow: {
      bg: 'bg-gradient-to-br from-white to-yellow-50/20 border-yellow-100/30',
      iconBg: 'bg-gradient-to-br from-yellow-500 to-yellow-600 text-white',
      badge: 'bg-yellow-100/60 text-yellow-700 border-yellow-200/40',
      text: 'text-yellow-600'
    }
  };

  return (
    <div className="ui-page animate-fade-in">
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-800 tracking-tight">
              Inventory Control
            </h1>
            <p className="text-slate-500 mt-1.5 font-medium">Monitor stock levels, edit details and track product expiration logs.</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Filter Category:</span>
              <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
                className="ui-input py-2 font-bold bg-white">
                {categories.map(c => (
                  <option key={c} value={c}>{c === 'all' ? 'All Categories' : c}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {[
          { label: 'Total Products', value: products.length, key: 'all',        color: 'blue',   icon: Package },
          { label: 'Total Stock Units', value: products.reduce((s, p) => s + p.stock, 0), key: 'totalStock', color: 'green', icon: Package },
          { label: 'Low Stock Level',   value: products.filter(p => p.stock < 40 && p.stock > 0).length, key: 'lowStock', color: 'orange', icon: AlertTriangle },
          { label: 'Out of Stock',   value: products.filter(p => p.stock === 0).length, key: 'outOfStock', color: 'red', icon: Package },
          { label: 'Expiring 30d',  value: products.filter(p => getDaysUntilExpiry(p.expiryDate) <= 30).length, key: 'expiring', color: 'yellow', icon: Calendar },
        ].map(card => {
          const Icon = card.icon;
          const active = filter === card.key;
          const styleConfig = colorMap[card.color];
          return (
            <div key={card.key} className="group cursor-pointer" onClick={() => { setFilter(card.key); setCurrentPage(1); }}>
              <div className={`ui-card p-5 border shadow-sm group-hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ${styleConfig.bg} ${active ? 'ring-2 ring-emerald-500/20 border-emerald-500/50' : ''}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className={`p-2.5 rounded-xl shadow-sm ${styleConfig.iconBg}`}>
                    <Icon size={18} />
                  </div>
                </div>
                <h3 className="text-slate-500 text-[10px] font-bold uppercase tracking-wider mb-1">{card.label}</h3>
                <p className={`text-xl font-extrabold tracking-tight ${styleConfig.text}`}>{card.value}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Table */}
      <div className="ui-table-wrap">
        <table className="ui-table">
          <thead>
            <tr>
              <th className="ui-th text-center w-24">Image</th>
              <th className="ui-th w-64">Product Name</th>
              <th className="ui-th w-40">Category</th>
              <th className="ui-th w-36 text-center">Batch Number</th>
              <th className="ui-th w-40 text-center">Stock Level</th>
              <th className="ui-th w-48 text-center">Expiry Date</th>
              <th className="ui-th w-32 text-center">Status</th>
              <th className="ui-th w-56 text-center">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {paginatedProducts.map(product => {
              const daysUntilExpiry = getDaysUntilExpiry(product.expiryDate);
              const isOutOfStock = product.stock === 0;
              const isCriticalStock = product.stock > 0 && product.stock < 20;
              const isLowStock = product.stock >= 20 && product.stock < 40;
              return (
                <tr key={product.id} className="hover:bg-slate-50/40 transition-colors duration-150 group">
                  <td className="ui-td text-center">
                    <div className="w-11 h-11 bg-slate-100 rounded-xl flex items-center justify-center mx-auto shadow-sm overflow-hidden border border-slate-200/50">
                      {product.imageUrl ? (
                        <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-xl">{product.image || '📦'}</span>
                      )}
                    </div>
                  </td>
                  <td className="ui-td">
                    <p className="font-extrabold text-slate-800 text-sm group-hover:text-emerald-600 transition-colors">{product.name}</p>
                    {product.weight != null && product.weight !== '' && (
                      <p className="text-[10px] text-slate-400 mt-1 font-bold uppercase tracking-wider">{product.weight} {product.stockUnit || product.weightUnit || 'kg'}</p>
                    )}
                  </td>
                  <td className="ui-td">
                    <span className="text-slate-500 font-semibold text-xs uppercase bg-slate-100 border border-slate-200/40 px-2.5 py-1 rounded-md">{product.category}</span>
                  </td>
                  <td className="ui-td text-center">
                    <span className="font-mono text-xs text-slate-600 bg-slate-150/70 border border-slate-200/40 px-2 py-0.5 rounded-lg">{product.batchNumber || '—'}</span>
                  </td>
                  <td className="ui-td text-center">
                    <div className="flex flex-col items-center">
                      <span className={
                        isOutOfStock   ? 'badge-danger'
                        : isCriticalStock  ? 'badge-danger'
                        : isLowStock  ? 'badge-warning'
                        : 'badge-success'
                      }>
                        {isOutOfStock ? 'Out of Stock' : `${product.stock} units`}
                      </span>
                    </div>
                  </td>
                  <td className="ui-td text-center">
                    <p className="font-bold text-slate-800 text-xs">{product.expiryDate || '—'}</p>
                    {product.expiryDate && (
                      <p className={`text-[10px] font-bold uppercase tracking-wider mt-1 ${daysUntilExpiry <= 30 ? 'text-rose-600' : 'text-slate-400'}`}>
                        {daysUntilExpiry > 0 ? `${daysUntilExpiry} days remaining` : 'Expired'}
                      </p>
                    )}
                  </td>
                  <td className="ui-td text-center">
                    <div className="flex flex-col gap-1 items-center">
                      {isOutOfStock && <span className="badge-danger text-[9px] uppercase tracking-wider px-2 py-0.5 rounded">Out of Stock</span>}
                      {isCriticalStock && <span className="badge-danger text-[9px] uppercase tracking-wider px-2 py-0.5 rounded">Critical</span>}
                      {isLowStock && <span className="badge-warning text-[9px] uppercase tracking-wider px-2 py-0.5 rounded">Low</span>}
                      {daysUntilExpiry <= 30 && daysUntilExpiry > 0 && <span className="badge-info text-[9px] uppercase tracking-wider px-2 py-0.5 rounded">Expiring</span>}
                      {daysUntilExpiry <= 0 && product.expiryDate && <span className="badge-danger text-[9px] uppercase tracking-wider px-2 py-0.5 rounded">Expired</span>}
                      {!isOutOfStock && !isCriticalStock && !isLowStock && (daysUntilExpiry > 30 || !product.expiryDate) && <span className="badge-success text-[9px] uppercase tracking-wider px-2 py-0.5 rounded">Good</span>}
                    </div>
                  </td>
                  {/* Action Buttons */}
                  <td className="ui-td text-center">
                    <div className="flex items-center justify-center gap-1.5">
                       <button
                         onClick={() => setEditingProduct(product)}
                         className="flex items-center justify-center gap-1 w-20 py-1.5 bg-blue-50 border border-blue-100 hover:bg-blue-600 hover:border-blue-600 text-blue-600 hover:text-white font-bold text-[10px] uppercase tracking-wider rounded-xl transition-all cursor-pointer shadow-sm"
                       >
                         <Edit2 size={11} />
                         <span>Adjust</span>
                       </button>
                       <button
                         onClick={() => setHistoryProduct(product)}
                         className="flex items-center justify-center gap-1 w-20 py-1.5 bg-purple-50 border border-purple-100 hover:bg-purple-600 hover:border-purple-600 text-purple-600 hover:text-white font-bold text-[10px] uppercase tracking-wider rounded-xl transition-all cursor-pointer shadow-sm"
                       >
                         <History size={11} />
                         <span>Logs</span>
                       </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {paginatedProducts.length === 0 && (
              <tr>
                <td colSpan={8} className="ui-td text-center py-12 text-slate-400 font-semibold">
                  No inventory products found matching filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={filteredProducts.length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* Edit Stock Modal */}
      {editingProduct && (
        <EditStockModal
          product={editingProduct}
          onClose={() => setEditingProduct(null)}
          onSave={handleStockSave}
        />
      )}

      {/* Stock History Modal */}
      {historyProduct && (
        <StockHistoryModal
          product={historyProduct}
          onClose={() => setHistoryProduct(null)}
        />
      )}
    </div>
  );
};

export default Inventory;